import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Modal, Form, Input, Select, Radio, Space, Flex, Segmented, Alert, Button } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import './AddUserModal.css';
import { FormContainer } from '@leedpulse-monorepo/components';
import { billingIntervalAsSuffix, EBillingInterval } from '@leedpulse-monorepo/enums';
import PlansContext from '../../../../contexts/plans/PlansContext';
import AuthenticationContext from '../../../../contexts/auth/AuthenticationContext';
import CompanyContext from '../../../../contexts/company/CompanyContext';
import getCrmUsers, { CrmUser } from '../../../../../domain/use_cases/integrations/getCrmUsers';
import { formatPlanLeedbucks } from '../../../../helpers';
import { useLeedbucksConfig } from '../../../../hooks/plans/useLeedbucksConfig';
import { ContactsTransferAnswer, ContactsTransferQuestion, getContactsTransferChoices } from '../../../../hooks/subscription/useNumberAssignmentSteps';
import ContactsTransferText from '../../../@shared/number-assignment/ContactsTransferText';
import NumberAssignmentProgress from '../../../@shared/number-assignment/NumberAssignmentProgress';

// TODO : @MrWarzo centraliser
interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
  maxDigits: number;
}

// TODO : @MrWarzo centraliser
const COUNTRIES: Country[] = [
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷', maxDigits: 9 },
  { code: 'DE', name: 'Allemagne', dialCode: '+49', flag: '🇩🇪', maxDigits: 11 },
  { code: 'CH', name: 'Suisse', dialCode: '+41', flag: '🇨🇭', maxDigits: 9 },
  { code: 'GB', name: 'Royaume-Uni', dialCode: '+44', flag: '🇬🇧', maxDigits: 10 },
  { code: 'BE', name: 'Belgique', dialCode: '+32', flag: '🇧🇪', maxDigits: 9 },
];

interface AddUserModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  /** Whether the company has a valid payment method on Stripe */
  hasValidPayment?: boolean;
  /** Whether the company's billing is handled externally by a partner (e.g. Septeo) */
  isCompanyExternallyBilled?: boolean;
  /** Company ID — used to fetch CRM users when partnerSystem is set */
  companyId?: string;
  /** CRM system identifier (e.g. 'apimo'). When provided, shows the CRM mode toggle. */
  partnerSystem?: string | null;
  onCancel: () => void;
  onSubmit: (values: AddUserFormValues) => void;
  availableNumbers?: { value: string; label: string }[];
  /** Asked once the user is created, when other users hold contacts on the chosen number */
  transferQuestion?: ContactsTransferQuestion | null;
  /** The backend is moving the number and its contacts */
  isAssigning?: boolean;
  onAnswerTransfer: (transfer: ContactsTransferAnswer) => void;
}

export interface AddUserFormValues {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  intro: string;
  numberAssignment: 'existing' | 'new' | 'none';
  id_plan?: string; // Only set when numberAssignment === 'new'
  selectedNumber?: string; // Only set when numberAssignment === 'existing'
  crmUserId?: string; // Set when the user was selected from CRM (mode === 'crm')
}

type NumberAssignmentType = 'existing' | 'new' | 'none';

const DEFAULT_COUNTRY = COUNTRIES[0];

const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  isLoading = false,
  hasValidPayment = false,
  isCompanyExternallyBilled = false,
  companyId,
  partnerSystem,
  onCancel,
  onSubmit,
  availableNumbers = [],
  transferQuestion = null,
  isAssigning = false,
  onAnswerTransfer,
}) => {
  const { plans, fetchPlans } = useContext(PlansContext);
  const { authentication } = useContext(AuthenticationContext);
  const { activeCompany } = useContext(CompanyContext);
  const { callRate, smsRate } = useLeedbucksConfig();

  // This modal orders a real number: a point of sale bills on one rhythm, so only the plans of
  // that rhythm may show up. The backend refuses a mixed order, but by then the user is created.
  const wantedInterval = activeCompany?.billing?.interval ?? null;
  const orderablePlans = useMemo(
    () => (wantedInterval ? plans.filter((plan) => (plan.price?.interval ?? EBillingInterval.MONTH) === wantedInterval) : plans),
    [plans, wantedInterval]
  );

  const [selectedCountry, setSelectedCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [localNumber, setLocalNumber] = useState<string>("");

  // CRM mode state
  const [mode, setMode] = useState<'manual' | 'crm'>('manual');
  const [crmUsers, setCrmUsers] = useState<CrmUser[]>([]);
  const [crmLoading, setCrmLoading] = useState(false);
  const [crmError, setCrmError] = useState<string | null>(null);
  const [selectedCrmUser, setSelectedCrmUser] = useState<CrmUser | null>(null);
  const [crmSelectError, setCrmSelectError] = useState<string | null>(null);

  const [form] = Form.useForm();
  const [numberAssignment, setNumberAssignment] = useState<NumberAssignmentType>(
    hasValidPayment ? 'new' : (availableNumbers.length > 0 ? 'existing' : 'none')
  );

  useEffect(() => {
    if (isOpen) {
      setNumberAssignment('none');
      form.setFieldValue('selectedNumber', undefined);
      form.setFieldValue('id_plan', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleModeChange = async (newMode: 'manual' | 'crm') => {
    setMode(newMode);
    setSelectedCrmUser(null);
    setCrmSelectError(null);

    if (newMode === 'crm' && companyId && authentication && crmUsers.length === 0 && !crmLoading) {
      setCrmLoading(true);
      setCrmError(null);
      try {
        const users = await getCrmUsers(companyId, authentication);
        setCrmUsers(users);
      } catch (e) {
        setCrmError((e as Error).message || 'Erreur lors du chargement des utilisateurs CRM');
      } finally {
        setCrmLoading(false);
      }
    }
  };

  const handleCrmUserSelect = (userId: string) => {
    const user = crmUsers.find(u => u.id === userId) ?? null;
    setSelectedCrmUser(user);
    setCrmSelectError(null);
  };

  const handleNumberAssignmentChange = (e: RadioChangeEvent) => {
    setNumberAssignment(e.target.value);
    if (e.target.value !== 'existing') {
      form.setFieldValue('selectedNumber', undefined);
    }
    if (e.target.value !== 'new') {
      form.setFieldValue('id_plan', undefined);
    }
  };

  const handleSubmit = () => {
    if (mode === 'crm') {
      if (!selectedCrmUser) {
        setCrmSelectError('Veuillez sélectionner un utilisateur CRM');
        return;
      }
      form.validateFields().then((values) => {
        onSubmit({
          ...values,
          firstname: selectedCrmUser.firstname,
          lastname: selectedCrmUser.lastname,
          email: selectedCrmUser.email,
          phone: selectedCrmUser.phone || '',
          intro: '',
          numberAssignment,
          crmUserId: selectedCrmUser.id,
        });
      });
    } else {
      form.validateFields().then((values) => {
        onSubmit({
          ...values,
          numberAssignment,
        });
      });
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setNumberAssignment(hasValidPayment ? 'new' : (availableNumbers.length > 0 ? 'existing' : 'none'));
    setLocalNumber("");
    setSelectedCountry(DEFAULT_COUNTRY);
    setMode('manual');
    setSelectedCrmUser(null);
    setCrmSelectError(null);
    onCancel();
  };

  const handleCountryChange = (country: Country) => {
    setSelectedCountry(country);

    const truncatedNumber = localNumber.slice(0, country.maxDigits);
    setLocalNumber(truncatedNumber);
    form.setFieldValue('phone', country.dialCode + truncatedNumber);
  };

  const handleNumberChange = (value: string) => {
    if (value.length > 0 && value[0] === '0') return;
    const cleanValue = value.replace(/[^0-9]/g, '');

    const limitedValue = cleanValue.slice(0, selectedCountry.maxDigits);
    setLocalNumber(limitedValue);
    form.setFieldValue('phone', selectedCountry.dialCode + limitedValue);
  };

  useEffect(() => {
    void fetchPlans();
  }, [fetchPlans]);

  // Once the question is answered, the modal stays open until the number moved. Before, closing it cancels: nothing is written
  const isLocked = isAssigning;
  const transferChoices = transferQuestion ? getContactsTransferChoices(transferQuestion) : null;

  return (
    <Modal
      title="Inviter un utilisateur"
      open={isOpen}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText="Envoyer l'invitation"
      cancelText="Annuler"
      confirmLoading={isLoading}
      centered
      width={520}
      className="add-user-modal"
      okButtonProps={{ className: 'add-user-modal__submit-btn' }}
      cancelButtonProps={{ className: 'add-user-modal__cancel-btn' }}
      closable={!isLocked}
      maskClosable={!isLocked}
      keyboard={!isLocked}
      footer={isAssigning ? null : transferChoices ? [
        <Button key="secondary" className="add-user-modal__cancel-btn" onClick={() => onAnswerTransfer(transferChoices.secondary.answer)}>
          {transferChoices.secondary.label}
        </Button>,
        <Button key="primary" type="primary" className="add-user-modal__submit-btn" onClick={() => onAnswerTransfer(transferChoices.primary.answer)}>
          {transferChoices.primary.label}
        </Button>,
      ] : undefined}
    >
      {isAssigning && <NumberAssignmentProgress />}
      {!isAssigning && transferQuestion && (
        <Alert
          type="warning"
          showIcon
          icon={<TeamOutlined />}
          message="Contacts déjà sur ce numéro"
          description={<ContactsTransferText question={transferQuestion} />}
        />
      )}
      <Form
        hidden={isLocked || Boolean(transferQuestion)}
        form={form}
        layout="vertical"
        requiredMark={(label, { required }) => (
          <>
            {required && <span className="add-user-modal__required-mark">*</span>}
            {label}
          </>
        )}
        className="add-user-modal__form"
      >
        {/* CRM / Manual mode toggle — only shown when company has a partner system */}
        {partnerSystem && (
          <FormContainer oneColumnMax>
            <Segmented
              options={[
                { label: 'Manuel', value: 'manual' },
                { label: 'Depuis le CRM', value: 'crm' },
              ]}
              value={mode}
              onChange={(val) => handleModeChange(val as 'manual' | 'crm')}
              block
              className="add-user-modal__mode-toggle"
            />
          </FormContainer>
        )}

        {mode === 'crm' ? (
          <FormContainer oneColumnMax>
            {crmError && (
              <Alert
                message={crmError}
                type="error"
                showIcon
                className="add-user-modal__crm-alert"
              />
            )}
            <Form.Item
              label="Utilisateur CRM"
              required
              className="add-user-modal__field"
              validateStatus={crmSelectError ? 'error' : undefined}
              help={crmSelectError ?? undefined}
            >
              <Select
                placeholder="Sélectionner un utilisateur"
                loading={crmLoading}
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={crmUsers
                  .filter(u => !u.alreadyProvisioned)
                  .map(u => ({
                    value: u.id,
                    label: `${u.firstname} ${u.lastname.toUpperCase()} (${u.email})`,
                  }))}
                onChange={handleCrmUserSelect}
                notFoundContent={
                  crmLoading
                    ? 'Chargement...'
                    : crmError
                    ? 'Erreur de chargement'
                    : 'Aucun utilisateur CRM disponible'
                }
              />
            </Form.Item>
          </FormContainer>
        ) : (
          <>
        <FormContainer twoColumnsMax>
          <Form.Item
            label="Prénom"
            name="firstname"
            rules={[{ required: true, message: 'Le prénom est requis' }]}
            className="add-user-modal__field"
          >
            <Input placeholder="Jean" />
          </Form.Item>
          <Form.Item
            label="Nom"
            name="lastname"
            rules={[{ required: true, message: 'Le nom est requis' }]}
            className="add-user-modal__field"
          >
            <Input placeholder="Dupont" />
          </Form.Item>
        </FormContainer>

        <FormContainer twoColumnsMax>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "L'email est requis" },
              { type: 'email', message: "L'email n'est pas valide" },
            ]}
            className="add-user-modal__field"
          >
            <Input placeholder="jean.dupont@agence.fr" />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {() => (
              <Form.Item
                label="Téléphone personnel"
                className="add-user-modal__field"
                validateStatus={form.getFieldError('phone').length > 0 ? 'error' : undefined}
                help={form.getFieldError('phone').length > 0 ? form.getFieldError('phone')[0] : undefined}
              >
                <Form.Item
                  name="phone"
                  rules={[{ required: true, message: 'Le téléphone est requis' }]}
                  hidden
                >
                  <Input />
                </Form.Item>
                <Flex gap="8px">
                  <Select
                    style={{ width: 'min-content' }}
                    options={COUNTRIES.map((country) => ({
                      label: country.flag + ' ' + country.dialCode,
                      value: country.code,
                      country: country,
                    }))}
                    value={selectedCountry.code}
                    onChange={(value, option) =>
                      option && handleCountryChange((option as unknown as { country: Country }).country)
                    }
                    optionRender={(option) => (
                      <Flex gap="8px">
                        <span>{(option.data.country.flag)}</span>
                        <span>{(option.data.country.name)}</span>
                        <span style={{ marginLeft: 'auto' }}>{(option.data.country.dialCode)}</span>
                      </Flex>
                    )}
                    popupMatchSelectWidth={false}
                  />
                  <Input
                    value={localNumber}
                    onChange={(e) => handleNumberChange(e.target.value)}
                    placeholder={`${selectedCountry.code === 'FR' ? '612345678' : ''}`}
                    maxLength={selectedCountry.maxDigits}
                    autoFocus
                    allowClear
                    status={localNumber.length > 0 && localNumber.length < selectedCountry.maxDigits ? 'error' : undefined}
                  />
                </Flex>
              </Form.Item>
            )}
          </Form.Item>
        </FormContainer>

        {/* <FormContainer oneColumnMax>
          <Form.Item
            label="Description (pour l'IA)"
            name="intro"
            className="add-user-modal__description-field"
          >
            <TextArea
              placeholder="Rôle, Métier, missions, objectifs, contexte géographique, contraintes spécifiques..."
              onChange={(e) => form.setFieldValue('intro', e.target.value)}
              rows={3}
            />
            <span className="add-user-modal__helper-text">
              Cette description aide l'IA à personnaliser les conversations et analyses.
            </span>
          </Form.Item>
        </FormContainer> */}
          </>
        )}

        <FormContainer oneColumnMax>
          <Form.Item label="Assignation d'un plan et numéro" name="numberAssignment" className="add-user-modal__description-field">
            <Radio.Group
              value={numberAssignment}
              onChange={handleNumberAssignmentChange}
              className="add-user-modal__radio-group"
            >
              <Space direction="vertical" className="add-user-modal__radio-space">
                <div
                  className={`add-user-modal__radio-option ${numberAssignment === 'existing' ? 'add-user-modal__radio-option--selected' : ''
                    }`}
                >
                  <Radio value="existing" disabled={availableNumbers.length === 0}>
                    Assigner un numéro existant
                    {availableNumbers.length === 0 && (
                      <span className="add-user-modal__radio-hint"> (aucun numéro disponible)</span>
                    )}
                  </Radio>
                  {numberAssignment === 'existing' && (
                    <Form.Item
                      name="selectedNumber"
                      className="add-user-modal__number-select-field"
                      rules={[
                        {
                          required: numberAssignment === 'existing',
                          message: 'Veuillez sélectionner un numéro',
                        },
                      ]}
                    >
                      <Select
                        placeholder="Sélectionner un numéro"
                        options={availableNumbers}
                        className="add-user-modal__number-select"
                      />
                    </Form.Item>
                  )}
                </div>

                <div
                  className={`add-user-modal__radio-option ${numberAssignment === 'new' ? 'add-user-modal__radio-option--selected' : ''
                    }`}
                >
                  <Radio value="new" disabled={isCompanyExternallyBilled || !hasValidPayment}>
                    Commander un nouveau numéro
                    {isCompanyExternallyBilled ? (
                      <span className="add-user-modal__radio-hint"> (pour commander un ou plusieurs numéros, merci de contacter directement votre interlocuteur Modelo)</span>
                    ) : !hasValidPayment ? (
                      <span className="add-user-modal__radio-hint"> (moyen de paiement requis)</span>
                    ) : null}
                  </Radio>
                  {numberAssignment === 'new' && (
                    <Form.Item
                      name="id_plan"
                      className="add-user-modal__number-select-field"
                      rules={[
                        {
                          required: numberAssignment === 'new',
                          message: 'Veuillez sélectionner un plan',
                        },
                      ]}
                    >
                      <Select
                        placeholder="Sélectionner un plan"
                        options={orderablePlans.map((plan) => ({
                          value: plan.id,
                          label: `${plan.name ?? plan.id} - ${plan.price?.formatted || '0€'}${billingIntervalAsSuffix(plan.price?.interval ?? EBillingInterval.MONTH)} (${formatPlanLeedbucks(plan, callRate, smsRate)})`,
                        }))}
                        onChange={(value) => form.setFieldValue('id_plan', value)}
                        className="add-user-modal__number-select"
                      />
                    </Form.Item>
                  )}
                </div>

                <div
                  className={`add-user-modal__radio-option ${numberAssignment === 'none' ? 'add-user-modal__radio-option--selected' : ''
                    }`}
                >
                  <Radio value="none">Ne pas assigner de numéro</Radio>
                  <p className="add-user-modal__radio-description">
                    L'utilisateur pourra se connecter mais ne pourra pas passer d'appel
                  </p>
                </div>
              </Space>
            </Radio.Group>
          </Form.Item>
        </FormContainer>
      </Form>
    </Modal>
  );
};

export default AddUserModal;
